import { memo, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCompany } from '../../hooks/useCompany';
import { useEditLicenseMutation } from '../../services/license.api';
import { SCOPE_TYPE } from '../../services/utils';
import {
  EditLicenseRequest,
  License,
  LicenseEditRequestBody,
  LicensePatchRequestUrlParam,
} from '../../stores/types/licenses.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  CheckBox,
  ColorType,
  Dropdown,
  DropdownType,
  FormInput,
  Label,
  LabelType,
  useGlobalModalContext,
} from '../_ui';
import { Form } from '../_ui/Form.component';
import { InputType } from '../_ui/Input.component';
import {
  DURATION_TYPES,
  durationTypeValues,
  getInternalLicenseType,
  getLicenseType,
  isLicenseTypeHome,
  isLicenseTypeHomeEssentialOrEla,
  isNoDurationType,
  LICENSE_TYPES,
  PORT_COUNT,
} from './utils';

export interface Props {
  license: License;
}

export const LicenseEdit = memo(({ license }: Props) => {
  const { t } = useTranslation();
  const { userScope } = useCompany();
  const { hideModal } = useGlobalModalContext();
  const [triggerEditLicense, editLicenseQueryResult] = useEditLicenseMutation();
  const [_license, setLicense] = useState<LicenseEditRequestBody>({
    licenseKey: license.licenseKey,
    type: getLicenseType(license.type),
    includes4G: license.includes4G,
    duration: license.duration,
    ports: license.ports,
    orderNote: license.orderNote,
  });

  const licenseTypeDropdown = useMemo(() => {
    return Object.values(LICENSE_TYPES).map((type) => ({
      id: type,
      label: t(`${type}`),
      selected: getLicenseType(_license.type) === type,
    }));
  }, [_license]);

  const licenseTypeHome = useMemo(() => {
    return isLicenseTypeHome(_license.type);
  }, [_license]);

  const portsPerLicenseDropdown = useMemo(() => {
    const availablePorts = licenseTypeHome
      ? [PORT_COUNT.ONE]
      : [PORT_COUNT.ONE, PORT_COUNT.TWO];
    return Object.values(availablePorts).map((ports) => ({
      id: ports,
      label: `${ports}`,
      selected: _license.ports === ports,
    }));
  }, [_license, licenseTypeHome]);

  const disableDuration = useMemo(() => {
    return isNoDurationType(_license.type);
  }, [_license]);

  const licenseDurationDropdown = useMemo(() => {
    return Object.values(DURATION_TYPES).map((duration) => ({
      id: duration,
      label: t(`${duration}`),
      selected:
        _license.duration ===
        durationTypeValues[
          duration?.toUpperCase() as keyof typeof durationTypeValues
        ],
      disabled: disableDuration,
    }));
  }, [_license, disableDuration]);

  const handleInputChange = (key: string, value: any) => {
    if (key === 'type') {
      setLicense({
        ..._license,
        [key]: value,
        duration:
          _license.duration &&
          Object.values(durationTypeValues).includes(_license.duration) &&
          !isLicenseTypeHomeEssentialOrEla(value)
            ? _license.duration
            : undefined,
      });
    } else {
      setLicense({
        ..._license,
        [key]: value,
      });
    }
  };

  const renderDuration = () => {
    return (
      <div className='flex flex-row'>
        <div className='flex-1 flex flex-col gap-2'>
          <div className='flex flex-row gap-2 items-baseline'>
            <Label
              text={t('duration')}
              type={LabelType.LABEL_M}
              color={ColorType.BLACK}
            />
          </div>
          <Dropdown
            placeholder={t('duration')}
            placeholderLabelType={LabelType.BODY3_G4}
            headerWidth={490}
            items={licenseDurationDropdown}
            type={DropdownType.SELECT}
            contentDivWidth={490}
            labelType={LabelType.BODY3}
            labelTypeHighlighted={LabelType.BODY3}
            chevdownIconHighlightColor={ColorType.GREY6}
            headerHighLightClassName='bg-grey1 border-grey3 rounded'
            onItemClick={(item: any) =>
              handleInputChange(
                'duration',
                durationTypeValues[
                  item.id?.toUpperCase() as keyof typeof durationTypeValues
                ],
              )
            }
            disabled={disableDuration}
          />
        </div>
      </div>
    );
  };

  const transformLicenseData = (licenseToTransform: LicenseEditRequestBody) => {
    const updatedLicense = {
      ...licenseToTransform,
      duration: isLicenseTypeHomeEssentialOrEla(licenseToTransform.type)
        ? undefined
        : licenseToTransform.duration,
      type: getInternalLicenseType(
        licenseToTransform.type,
        licenseToTransform.ports,
      ),
    };
    return updatedLicense;
  };

  const handleSave = () => {
    const urlParams: LicensePatchRequestUrlParam = {
      scope: userScope || SCOPE_TYPE.COMPANY,
      companyId: license.companyId,
      bulkOperation: false,
    };

    const editPayload: EditLicenseRequest = {
      licenseEditRequestBody: transformLicenseData(_license),
      licenseEditRequestUrlParam: urlParams,
    };
    triggerEditLicense(editPayload);
  };

  const onSaveValidation = () => {
    if (
      !_license?.type ||
      !_license?.ports ||
      (!disableDuration && !_license?.duration)
    ) {
      return false;
    }
    return true;
  };

  return (
    <Form
      onSubmit={handleSave}
      queryResult={editLicenseQueryResult}
      onQuerySuccess={() => hideModal()}
      onQueryFailed={() => hideModal()}
    >
      <div className='flex flex-col justify-between h-full'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-row'>
            <div className='flex-1 flex flex-col gap-2'>
              <div className='flex flex-row gap-2 items-baseline'>
                <Label
                  text={t('license_type_placeholder')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
              </div>
              <Dropdown
                placeholder={t('license_type_placeholder')}
                placeholderLabelType={LabelType.BODY3_G4}
                headerWidth={240}
                items={licenseTypeDropdown}
                type={DropdownType.SELECT}
                contentDivWidth={240}
                labelType={LabelType.BODY3}
                labelTypeHighlighted={LabelType.BODY3}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded'
                onItemClick={(item: any) => handleInputChange('type', item.id)}
              />
            </div>
            <div className='flex-1 flex flex-col gap-2'>
              <div className='flex flex-row gap-2 items-baseline'>
                <Label
                  text={t('ports_per_license')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
              </div>
              <Dropdown
                placeholder={t('ports_per_license')}
                placeholderLabelType={LabelType.BODY3_G4}
                headerWidth={240}
                items={portsPerLicenseDropdown}
                type={DropdownType.SELECT}
                contentDivWidth={240}
                labelType={LabelType.BODY3}
                labelTypeHighlighted={LabelType.BODY3}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='bg-grey1 border-grey3 rounded'
                onItemClick={(item: any) => handleInputChange('ports', item.id)}
              />
            </div>
          </div>
          {renderDuration()}
          <div className='flex flex-row'>
            <div className='flex-1 flex flex-col gap-2'>
              <div className='flex flex-row gap-2 items-baseline'>
                <Label
                  text={t('license_includes_chargelab_4g')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                />
              </div>
              <CheckBox
                label={t('yes')}
                selected={_license.includes4G}
                onChange={(value: boolean) =>
                  handleInputChange('includes4G', value)
                }
              />
            </div>
          </div>
          <div className='flex flex-row'>
            <div className='flex-1 flex flex-col gap-2'>
              <div className='flex flex-row gap-2 items-baseline'>
                <FormInput
                  inputId='order_note'
                  defaultValue={_license?.orderNote}
                  label={t('order_note')}
                  secondLabel={`(${t('chargers_optional')})`}
                  secondLabelType={LabelType.BODY2}
                  secondLabelColor={ColorType.GREY6}
                  placeholder={t('order_note')}
                  inputType={InputType.TEXT}
                  props={{ style: { width: '490px' } }}
                  onChange={(event: any) =>
                    handleInputChange('orderNote', event.target.value)
                  }
                />
              </div>
            </div>
          </div>
          <div className='mb-[20px] w-[490px]'>
            <Button
              label={t('save')}
              size={ButtonSize.SMALL_FULL}
              type={ButtonType.PRIMARY}
              disabled={!onSaveValidation()}
              isSumbit
              dataTestId='licenseEditSave'
            />
          </div>
        </div>
      </div>
    </Form>
  );
});

LicenseEdit.displayName = 'LicenseEdit';
